import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { expValEquals } from '@confluence/feature-experiments';
import { type UseWriteFragmentInteropResult } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';
import { type UseReadFragmentInteropResult } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop';

import { StarredSpacesQuery } from './SpacesListQueries.graphql';
import type {
	StarredSpacesQuery as StarredSpacesQueryData,
	StarredSpacesQuery_spaces_PaginatedSpaceList,
	StarredSpacesQuery_spaces_PaginatedSpaceList_nodes_Space as spaceType,
} from './__types__/StarredSpacesQuery';

export const updateStarredSpacesList = (
	spaceId: string,
	isStarred: boolean,
	readCurrentSpace: UseReadFragmentInteropResult<spaceType>,
	readStarredSpaces: UseReadFragmentInteropResult<StarredSpacesQuery_spaces_PaginatedSpaceList>,
	writeStarredSpacesFragment: UseWriteFragmentInteropResult<StarredSpacesQuery_spaces_PaginatedSpaceList>,
) => {
	const apolloClient = getApolloClient();

	const space = readCurrentSpace({
		id: `Space:${spaceId}`,
	});

	try {
		let starredSpaces;
		if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
			starredSpaces =
				readStarredSpaces({
					id: `$ROOT_QUERY.spaces(${JSON.stringify({ favourite: true, first: 99, status: 'current' })})`,
				})?.nodes || [];
		} else {
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			starredSpaces =
				apolloClient.readQuery<StarredSpacesQueryData>({
					query: StarredSpacesQuery,
					variables: {},
				})?.spaces?.nodes || [];
		}
		let newStarredSpaces;
		if (isStarred) {
			newStarredSpaces = [...starredSpaces, space];
			newStarredSpaces.sort((a, b) => (!a?.name || !b?.name ? 0 : a.name.localeCompare(b.name)));
		} else {
			newStarredSpaces = starredSpaces.filter((s: any) => s?.id !== spaceId);
		}

		if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
			writeStarredSpacesFragment({
				fragmentId: `$ROOT_QUERY.spaces(${JSON.stringify({ favourite: true, first: 99, status: 'current' })})`,
				data: { nodes: newStarredSpaces, __typename: 'PaginatedSpaceList' },
			});
		} else {
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			apolloClient.writeQuery({
				query: StarredSpacesQuery,
				variables: {},
				data: {
					spaces: { nodes: newStarredSpaces, __typename: 'PaginatedSpaceList' },
				},
			});
		}
	} catch (e) {
		// If Apollo throws an error, the starred spaces data was not found in the cache.
		// Errors can be safely ignored as if there is no cache entry, there is no work to be done.
	}
};
